import { useEffect, useRef, useState } from "react";
import RecorderForm, {
  type RecorderFormHandle,
} from "@/components/ui/audio-recorder/recorder-form";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import {
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { Button } from "@/components/ui/button";
import { t } from "i18next";
import { Loader } from "lucide-react";
import { useSyncRecordingTranscription } from "@/hooks/linkedRecordingSession/useSyncRecordingTranscription";
import { useVisitSubmit } from "@/hooks/visits/useVisitSubmit";
import { useAuthStore } from "@/store/auth.store";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { v4 as uuidv4 } from "uuid";
import { TranscriptionStreamingList } from "../../audio-recorder/TranscriptionStreamingList";
import { toast } from "@/hooks/use-toast";
import type { LinkedRecordingSession } from "@/services/linkedRecordingSessions";

interface SyncRecordingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linkedRecordingSession: LinkedRecordingSession;
}

export const SyncRecordingSheet = (
  props: SyncRecordingSheetProps
): JSX.Element => {
  const { onOpenChange } = props;

  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { isProviderReady, finished, transcription } =
    useSyncRecordingTranscription({
      roomId: props.linkedRecordingSession.id,
    });
  const { submitVisitWithoutFile, error, loading, result } = useVisitSubmit();
  const { userData } = useAuthStore.getState();

  useEffect(() => {
    if (!loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("visits.sidebar.messages.uploadSuccess.title"),
          description: t("visits.sidebar.messages.uploadSuccess.description"),
        });
        onOpenChange(false);
      }
      if (error) {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: t("visits.sidebar.messages.requestFailed.description"),
        });
      }
    }
  }, [result, error, loading, onOpenChange]);

  const handleFormSubmit = async (
    formData: RecorderFormSchemaType
  ): Promise<void> => {
    if (!userData?.id) return;
    if (!transcription) {
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description: t("visits.sidebar.messages.noTranscription"),
      });
      return;
    }
    await submitVisitWithoutFile({
      user: userData.id.toString(),
      name: uuidv4(),
      duration: transcription.duration,
      style: formData.style,
      format: formData.format,
      exam: formData.examination,
      template: formData.templateName,
      customTemplate: formData.customTemplateId ?? undefined,
      language: transcription.language,
      transcription: transcription.transcription,
    });
  };

  return (
    <SheetContent className="w-full sm:max-w-md p-0 sm:rounded-l-2xl">
      <SheetTitle></SheetTitle>
      <SheetDescription></SheetDescription>
      <SheetHeader />
      <div className="flex flex-col flex-1 h-full">
        <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
          <div className="w-full">
            <div className="text-sm text-center mb-4">
              <span className="font-bold">ID:</span>{" "}
              {props.linkedRecordingSession.id}
            </div>
            {isProviderReady && !finished && <TranscriptionStreamingList />}
            {!isProviderReady && !finished && (
              <div className="flex items-center gap-2 justify-center">
                {t("components.syncRecordingSheet.waitingProvider")}{" "}
                <Loader className="animate-spin" />
              </div>
            )}
            {finished && (
              <RecorderForm
                ref={recorderFormRef}
                onSubmitData={handleFormSubmit}
              />
            )}
          </div>
        </div>
        <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
          {finished && (
            <SubscriptionOnlyButton
              className="w-full"
              disabled={loading}
              size={"xl"}
              validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
              onClick={async () => {
                await recorderFormRef.current?.submitForm();
              }}
            >
              {t("visits.sidebar.submit")}
            </SubscriptionOnlyButton>
          )}
          <ConfirmDialog
            description={t("visits.sidebar.cancelConfirmation.description")}
            open={showCancelConfirmation}
            title={t("visits.sidebar.cancelConfirmation.title")}
            trigger={
              <Button className="w-full" size={"xl"} variant={"grey"}>
                {t("visits.sidebar.cancel")}
              </Button>
            }
            onOpenChange={setShowCancelConfirmation}
            onConfirm={() => {
              props.onOpenChange(false);
            }}
          />
        </SheetFooter>
      </div>
    </SheetContent>
  );
};
